import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Link, Skeleton, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { client, withOpenIncidents, yearlySaving, type Application, type Department, type ServiceStatus } from '../lib/api';
import { AppStatusChip, ServiceStatusChip } from '../components/StatusChip';
import { AppMonogram, Section } from '../components/Page';
import { useAuth } from '../lib/AuthContext';
import { tokens } from '../theme';
import { AppCover } from '../components/AppScreens';

const AUDIENCES = [
  {
    eyebrow: 'For staff',
    title: 'Use the tools',
    body: 'Open the apps IT Lab has built and see what each one does.',
    cta: 'Browse applications',
    to: '/apps',
  },
  {
    eyebrow: 'For department leads',
    title: 'Get something built',
    body: 'Describe a problem, book a scoping session, and track the request until it ships.',
    cta: 'Request an app',
    to: '/request',
  },
  {
    eyebrow: 'For everyone',
    title: 'Know what’s up',
    body: 'Live health and incidents for the portal and every IT Lab application.',
    cta: 'View status',
    to: '/status',
  },
];

const cardLinkSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.25,
  p: { xs: 2.5, md: 3 },
  border: `1px solid ${tokens.border}`,
  borderRadius: 2,
  textDecoration: 'none',
  color: 'text.primary',
  minHeight: 210,
  background: 'linear-gradient(145deg, #FFFFFF 45%, #F5F9FD 100%)',
  transition: 'border-color 120ms ease, transform 120ms ease',
  '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
  '&:hover .cta': { textDecoration: 'underline' },
} as const;

export function HomePage() {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catalog, status] = await Promise.all([client.catalog(), client.status()]);
        if (cancelled) return;
        setDepartments(catalog.departments);
        setApplications(catalog.applications);
        setServices(withOpenIncidents(status.services, status.incidents));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const overall = services.some((s) => s.status === 'outage')
    ? 'outage'
    : services.some((s) => s.status === 'degraded')
      ? 'degraded'
      : services.some((s) => s.status === 'maintenance')
        ? 'maintenance'
        : 'operational';
  const overallDotColor = overall === 'operational' ? '#12B76A' : overall === 'outage' ? '#F04438' : '#F79009';

  const firstName = user?.name?.split(' ')[0];
  const deptName = (id: string) => departments.find((d) => d.id === id)?.name ?? 'Department';

  const savings = applications.map(yearlySaving).filter((n): n is number => n !== null);
  const totalSaving = Math.round(savings.reduce((a, b) => a + b, 0) / 1000) * 1000;
  const stats = [
    { label: 'Applications', value: applications.length },
    { label: 'Departments served', value: departments.length },
    { label: 'Services monitored', value: services.length },
  ];

  return (
    <>
      {error && (
        <Section py={3}>
          <Alert severity="error">{error}</Alert>
        </Section>
      )}

      {/* Hero */}
      <Section band="subtle" py={{ xs: 6, md: 8 }} sx={{ borderTop: 0, overflow: 'hidden', position: 'relative' }}>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            width: 520,
            height: 520,
            right: { xs: -260, md: -100 },
            top: { xs: -250, md: -230 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(86,180,233,.15) 0%, rgba(86,180,233,0) 68%)',
            pointerEvents: 'none',
          }}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 5, md: 8 }} alignItems="center" sx={{ position: 'relative' }}>
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            {firstName && (
              <Typography fontWeight={600} color="primary.main">
                Welcome back, {firstName}
              </Typography>
            )}
            <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 58 }, letterSpacing: '-0.04em', maxWidth: 620 }}>
              Tools for the way AST works.
            </Typography>
            <Typography sx={{ fontSize: { xs: 17, md: 19 }, color: 'text.secondary', maxWidth: 530 }}>
              From finance and procurement to IT and production, find the apps built around your team’s work.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }} sx={{ pt: 0.5 }}>
              <Button
                component={RouterLink}
                to="/apps"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
              >
                Browse applications
              </Button>
              <Button component={RouterLink} to="/request" variant="outlined" size="large">
                Request an app
              </Button>
            </Stack>
            <Link component={RouterLink} to="/status" sx={{ alignSelf: 'flex-start', pt: 0.5 }}>
              Check live service status
            </Link>
          </Stack>

          <Box sx={{ flex: 1, width: '100%', minWidth: 0, maxWidth: 520 }}>
            <Box
              sx={{
                bgcolor: '#fff',
                border: `1px solid ${tokens.border}`,
                borderRadius: 2.5,
                boxShadow: '0 18px 48px rgba(16, 54, 89, .09)',
                overflow: 'hidden',
              }}
            >
              <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${tokens.divider}` }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: overallDotColor }} />
                <Typography fontSize={14} fontWeight={600} color={tokens.text2}>IT Lab services</Typography>
                <Box flex={1} />
                <ServiceStatusChip status={overall} />
              </Stack>
              <Stack spacing={1.5} sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ pb: 0.5 }}>
                  <Box>
                    <Typography fontSize={13} color="text.secondary">Applications for AST</Typography>
                    <Typography sx={{ fontSize: 40, lineHeight: 1.1, fontWeight: 800, letterSpacing: '-.04em', color: tokens.ink }}>
                      {loading ? '–' : applications.length}
                    </Typography>
                  </Box>
                  <Link component={RouterLink} to="/apps" fontSize={14} sx={{ pb: 0.75 }}>View catalog →</Link>
                </Stack>
                {loading
                  ? [1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={58} />)
                  : applications.slice(0, 3).map((app) => (
                      <Stack
                        key={app.id}
                        component={RouterLink}
                        to={`/apps/${app.slug}`}
                        direction="row"
                        alignItems="center"
                        gap={1.5}
                        sx={{
                          p: 1.25,
                          border: `1px solid ${tokens.divider}`,
                          borderRadius: 1.5,
                          textDecoration: 'none',
                          color: 'inherit',
                          transition: 'border-color 120ms ease, background-color 120ms ease',
                          '&:hover': { borderColor: 'primary.main', bgcolor: '#FAFCFE' },
                        }}
                      >
                        <AppMonogram name={app.name} slug={app.slug} logoUrl={app.logo_url} size="sm" />
                        <Box flex={1} minWidth={0}>
                          <Typography fontWeight={600} fontSize={14} color={tokens.ink} noWrap>{app.name}</Typography>
                          <Typography fontSize={12} color={tokens.text3} noWrap>{deptName(app.department_id)}</Typography>
                        </Box>
                        <AppStatusChip status={app.status} />
                      </Stack>
                    ))}
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Section>

      {/* Statement */}
      <Section band="subtle" id="about" py={{ xs: 6, md: 8 }}>
        <Stack spacing={5}>
          <Typography
            component="p"
            sx={{
              fontSize: { xs: 26, md: 36 },
              lineHeight: 1.2,
              fontWeight: 700,
              letterSpacing: '-0.025em',
              color: tokens.ink,
              maxWidth: 960,
            }}
          >
            We build the tools AST teams count on, move fast, and replace spreadsheets with software that
            just works.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={6}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Typography variant="h2" fontSize={22}>
                What we’ve built
              </Typography>
              <Typography color="text.secondary" maxWidth={480}>
                Each application is owned by IT Lab end to end: we scope it with the department, build it,
                and keep it running. Every app lists its features, stack and live health.
              </Typography>
              <Link component={RouterLink} to="/apps">
                See every application →
              </Link>
            </Stack>
            <Box
              component="dl"
              sx={{ flex: 1.2, m: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 3 }}
            >
              {stats.map((s) => (
                <Box key={s.label} sx={{ borderTop: `3px solid ${tokens.blue}`, pt: 2 }}>
                  <Typography component="dt" fontSize={14} color="text.secondary">
                    {s.label}
                  </Typography>
                  <Typography
                    component="dd"
                    sx={{ m: 0, fontSize: { xs: 36, md: 48 }, fontWeight: 800, letterSpacing: '-0.03em', color: tokens.ink }}
                  >
                    {loading ? '–' : s.value}
                  </Typography>
                </Box>
              ))}
              {!loading && totalSaving > 0 && (
                <Box sx={{ gridColumn: '1 / -1', bgcolor: tokens.tint, borderRadius: 2, px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 } }}>
                  <Typography component="dt" fontSize={14} fontWeight={600} color="primary.main">
                    Estimated saving for AST
                  </Typography>
                  <Typography
                    component="dd"
                    sx={{ m: 0, mt: 0.5, fontSize: { xs: 32, md: 44 }, fontWeight: 800, letterSpacing: '-0.03em', color: tokens.ink, lineHeight: 1.1 }}
                  >
                    ≈ S${totalSaving.toLocaleString('en-SG')}{' '}
                    <Box component="span" sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 600, letterSpacing: 0, color: tokens.text2 }}>
                      a year
                    </Box>
                  </Typography>
                  <Typography fontSize={13} color={tokens.text2} mt={1}>
                    Sum of the estimates for {savings.length} applications. Each app page shows how its figure was estimated;
                    projected savings for apps still in development are not included.
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>
      </Section>

      {/* Audiences */}
      <Section py={{ xs: 6, md: 8 }}>
        <Stack spacing={5}>
          <Stack spacing={1}>
            <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 } }}>
              A place for every team
            </Typography>
            <Typography fontSize={18} color="text.secondary">
              Start from what you need today.
            </Typography>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            {AUDIENCES.map((a) => (
              <Box key={a.title} component={RouterLink} to={a.to} sx={cardLinkSx}>
                <Typography fontSize={14} fontWeight={600} color="primary.main">
                  {a.eyebrow}
                </Typography>
                <Typography variant="h3" fontSize={22}>
                  {a.title}
                </Typography>
                <Typography color="text.secondary">{a.body}</Typography>
                <Typography className="cta" sx={{ mt: 'auto', pt: 1.5, fontWeight: 600, color: 'primary.main' }}>
                  {a.cta} →
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>
      </Section>

      {/* Applications */}
      <Box sx={{ borderTop: `1px solid ${tokens.divider}`, bgcolor: '#FBFCFE' }}>
        <Section py={{ xs: 6, md: 8 }}>
          <Stack spacing={4}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" gap={2} flexWrap="wrap">
              <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 } }}>
                Our applications
              </Typography>
              <Link component={RouterLink} to="/apps">
                Discover all applications →
              </Link>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 4 }}>
              {loading
                ? [1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={300} />)
                : applications.slice(0, 6).map((app) => (
                    <Stack
                      key={app.id}
                      spacing={1.5}
                      sx={{
                        borderRadius: 2,
                        '&:hover h3': { color: 'primary.main', textDecoration: 'underline' },
                      }}
                    >
                      <Box
                        component={RouterLink}
                        to={`/apps/${app.slug}`}
                        sx={{
                          display: 'block',
                          textDecoration: 'none',
                          color: 'inherit',
                          borderRadius: 2,
                          '&:focus-visible': { outline: `3px solid ${tokens.focus}`, outlineOffset: 3 },
                        }}
                      >
                        <AppCover app={app} variant="tile" />
                      </Box>
                      <Stack spacing={0.75} px={0.5}>
                        <Box
                          component={RouterLink}
                          to={`/apps?dept=${app.department_id}`}
                          sx={{
                            alignSelf: 'flex-start',
                            px: 1.25,
                            py: 0.25,
                            borderRadius: 999,
                            bgcolor: tokens.strip,
                            fontSize: 13,
                            fontWeight: 500,
                            color: '#344054',
                            textDecoration: 'none',
                            '&:hover': { color: 'primary.main', bgcolor: tokens.tint },
                          }}
                        >
                          {deptName(app.department_id)}
                        </Box>
                        <Typography
                          component={RouterLink}
                          to={`/apps/${app.slug}`}
                          variant="h3"
                          fontSize={20}
                          sx={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {app.name}
                        </Typography>
                        <Typography color="text.secondary">{app.tagline}</Typography>
                      </Stack>
                    </Stack>
                  ))}
            </Box>
          </Stack>
        </Section>
      </Box>

      {/* CTA band */}
      <Section band="brand" py={{ xs: 7, md: 10 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={4} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack spacing={1.5} maxWidth={600}>
            <Typography variant="h2" color="#fff" sx={{ fontSize: { xs: 28, md: 36 } }}>
              Have an idea for a tool?
            </Typography>
            <Typography fontSize={18} color="#D6E6F5">
              If your team is stuck in spreadsheets or manual steps, tell us. We’ll book a scoping session on
              a date that suits you.
            </Typography>
          </Stack>
          <Button
            component={RouterLink}
            to="/request"
            size="large"
            sx={{
              bgcolor: '#fff',
              color: tokens.blueDeep,
              fontWeight: 700,
              alignSelf: { xs: 'flex-start', md: 'center' },
              textDecoration: 'none',
              '&:hover': { bgcolor: '#EEF4FA' },
            }}
          >
            Request an app
          </Button>
        </Stack>
      </Section>
    </>
  );
}
